/**
 * MediaFormSheet.js — media-send preview + caption sheet (#121, #151).
 *
 * Pure presentational component extracted from Composer.js. Shows an
 * inline preview for images/videos, a filename-only card for
 * audio/documents, and an optional caption textarea (hidden for audio,
 * which Meta's Cloud API doesn't support captions on).
 *
 * Props:
 *   - form: { file, previewUrl, type, caption, pending } | null
 *   - setForm: (next) => void
 *   - onClose: () => void
 *   - onSubmit: () => Promise<void>
 *   - fmtBytes: (n) => string (pretty byte formatter)
 */

import { html } from 'htm/preact';

export function MediaFormSheet({ form, setForm, onClose, onSubmit, fmtBytes }) {
  if (!form) return null;

  const title =
      form.type === 'image'    ? '📷 Send photo'
    : form.type === 'video'    ? '🎬 Send video'
    : form.type === 'audio'    ? '🎵 Send audio'
    :                            '📄 Send document';

  const captionPlaceholder =
      form.type === 'image' ? 'Add a caption for this photo…'
    : form.type === 'video' ? 'Add a caption for this video…'
    :                         'Add a description for this file…';

  return html`
    <div class="inbox-sheet-overlay" style="display:block" onClick=${onClose}></div>
    <div
      class="inbox-bottom-sheet"
      style="display:flex;flex-direction:column;max-height:88vh;background:var(--v2-bg-elevated, #131722)"
      role="dialog"
      aria-modal="true"
      aria-label=${`Send ${form.type}`}
      onClick=${(e) => e.stopPropagation()}
    >
      <div class="inbox-sheet-handle"></div>
      <div class="inbox-sheet-header">
        <h3 class="inbox-sheet-title">${title}</h3>
        <button class="inbox-sheet-close" onClick=${onClose} aria-label="Cancel">×</button>
      </div>
      <div style="padding:12px 14px 16px;overflow-y:auto">
        <!-- Preview -->
        <div style="display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.35);border:1px solid rgba(255,255,255,0.08);border-radius:12px;padding:10px;margin-bottom:14px;min-height:140px">
          ${form.type === 'image' && form.previewUrl && html`
            <img
              src=${form.previewUrl}
              alt="preview"
              style="max-width:100%;max-height:40vh;border-radius:8px;object-fit:contain"
            />
          `}
          ${form.type === 'video' && form.previewUrl && html`
            <video
              src=${form.previewUrl}
              controls
              playsinline
              style="max-width:100%;max-height:40vh;border-radius:8px"
            ></video>
          `}
          ${(form.type === 'document' || form.type === 'audio') && html`
            <div style="display:flex;flex-direction:column;align-items:center;gap:8px;color:var(--v2-text, #f1f4f9);padding:10px 14px">
              <div style="font-size:2rem">${form.type === 'audio' ? '🎵' : '📄'}</div>
              <div style="font-weight:600;font-size:0.88rem;word-break:break-all;text-align:center">
                ${form.file.name}
              </div>
              <div style="font-size:0.72rem;color:var(--text-muted, #7d8799)">
                ${fmtBytes(form.file.size)}
              </div>
            </div>
          `}
        </div>

        <!-- Caption input. Audio skips this — Meta spec doesn't support
             audio captions, and the parent also zeroes the caption
             before dispatch as a belt-and-braces guarantee. -->
        ${form.type !== 'audio' && html`
          <label style="display:block;font-size:0.7rem;color:var(--text-muted, #7d8799);margin-bottom:4px">
            Caption (optional)
          </label>
          <textarea
            value=${form.caption}
            onInput=${(e) => setForm({ ...form, caption: e.target.value })}
            placeholder=${captionPlaceholder}
            rows="3"
            maxlength="1024"
            disabled=${form.pending}
            style="width:100%;background:rgba(0,0,0,0.3);border:1px solid rgba(255,255,255,0.12);border-radius:10px;padding:10px 12px;color:inherit;font-size:0.88rem;line-height:1.4;resize:vertical;font-family:inherit;margin-bottom:14px"
          ></textarea>
        `}

        <button
          onClick=${onSubmit}
          disabled=${form.pending}
          style="width:100%;background:#25D366;color:#0a0c12;border:none;border-radius:10px;padding:12px;font-size:0.9rem;font-weight:700;cursor:${form.pending ? 'wait' : 'pointer'};opacity:${form.pending ? 0.7 : 1};display:flex;align-items:center;justify-content:center;gap:8px"
        >
          ${form.pending
            ? 'Sending…'
            : html`
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
                Send
              `}
        </button>
      </div>
    </div>
  `;
}
